import type { BackofficeTrailDto, TrailFilter, TrailRequestDto } from '@kardume/types';
import { TRAIL_MIN_MERCHANTS } from '@kardume/types';
import { toLocalInput, fromLocalInput } from '../../utils/datetime';

/**
 * The trail form, kept apart from the screen that draws it (card #222).
 *
 * Everything here is pure: no React, no HTTP. That is what makes the two rules
 * that actually matter testable without a browser - the slug that must survive
 * a rename, and the filter that has to reach the preview in exactly the shape
 * it will be saved in.
 */

/**
 * What the fields hold while they are being edited.
 *
 * Numbers live as strings on purpose: an empty rating field is "don't care"
 * (`null` in the filter), and `Number('')` is `0`, which would silently become
 * "at least zero stars" - a criterion that filters nothing but looks like one.
 */
export interface TrailFormState {
  name: string;
  /** Blank on creation = the server derives it from the name. */
  slug: string;
  tagline: string;
  icon: string;
  /** `''` = every city. */
  localityId: string;
  amenities: string[];
  priceRanges: number[];
  minRating: string;
  minCashback: string;
  openNow: boolean;
  /** `datetime-local` strings; `''` = no bound on that side. */
  startsAt: string;
  endsAt: string;
  sortOrder: number;
  active: boolean;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string[];
}

export const emptyTrailForm = (): TrailFormState => ({
  name: '',
  slug: '',
  tagline: '',
  icon: '',
  localityId: '',
  amenities: [],
  priceRanges: [],
  minRating: '',
  minCashback: '',
  openNow: false,
  startsAt: '',
  endsAt: '',
  sortOrder: 0,
  active: true,
  seoTitle: '',
  seoDescription: '',
  seoKeywords: [],
});

/**
 * An existing trail into the form.
 *
 * The SEO block is flattened here - nested in the response, flat in the
 * request - and the slug is carried over as it is. It is **not** re-derived
 * from the name: that is the whole point of the rule below.
 */
export const toTrailForm = (trail: BackofficeTrailDto): TrailFormState => ({
  name: trail.name,
  slug: trail.slug,
  tagline: trail.tagline ?? '',
  icon: trail.icon ?? '',
  localityId: trail.localityId ?? '',
  amenities: trail.filter?.amenities ?? [],
  priceRanges: trail.filter?.priceRanges ?? [],
  minRating: trail.filter?.minRating != null ? String(trail.filter.minRating) : '',
  minCashback: trail.filter?.minCashback != null ? String(trail.filter.minCashback) : '',
  openNow: trail.filter?.openNow === true,
  startsAt: toLocalInput(trail.startsAt),
  endsAt: toLocalInput(trail.endsAt),
  sortOrder: trail.sortOrder,
  active: trail.active,
  seoTitle: trail.seo?.title ?? '',
  seoDescription: trail.seo?.description ?? '',
  seoKeywords: trail.seo?.keywords ?? [],
});

/** `'4,5'` and `'4.5'` are the same number; `''` is not a number at all. */
const toNumberOrNull = (raw: string): number | null => {
  const text = raw.trim().replace(',', '.');
  if (!text) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
};

/**
 * The filter in the canonical shape the server stores.
 *
 * The normalisation is **mirrored**, not invented: amenity codes upper-cased
 * and de-duplicated, price ranges sorted and de-duplicated, empty lists dropped
 * to `null`, and `openNow: false` dropped to `null` because `false` means "I
 * don't care" and never "only the closed ones".
 *
 * Mirroring earns its keep twice. The preview has to be asked with the filter
 * that will actually be saved, or the number on screen is not the number the
 * visitor gets; and [filterSignature] can only tell "this is the same filter"
 * from "this is a new one" if equivalent selections produce identical text.
 */
export const buildTrailFilter = (form: TrailFormState): TrailFilter => {
  const amenities = Array.from(
    new Set(form.amenities.map((code) => code.trim().toUpperCase()).filter(Boolean))
  );
  const priceRanges = Array.from(new Set(form.priceRanges)).sort((a, b) => a - b);
  return {
    amenities: amenities.length > 0 ? amenities : null,
    priceRanges: priceRanges.length > 0 ? priceRanges : null,
    minRating: toNumberOrNull(form.minRating),
    minCashback: toNumberOrNull(form.minCashback),
    openNow: form.openNow ? true : null,
  };
};

/** A filter with no criterion at all is `/empresas` under another name. */
export const isFilterEmpty = (filter: TrailFilter): boolean =>
  (filter.amenities == null || filter.amenities.length === 0) &&
  (filter.priceRanges == null || filter.priceRanges.length === 0) &&
  filter.minRating == null &&
  filter.minCashback == null &&
  filter.openNow !== true;

/**
 * A stable key for "is the preview on screen still about this filter?".
 *
 * Built over the canonical filter plus the city, because the count is scoped by
 * city too: the same filter catches a different number of stores in Toledo and
 * in Cascavel, and showing the Toledo number under a Cascavel trail is worse
 * than showing none.
 */
export const filterSignature = (filter: TrailFilter, localityId: string): string =>
  JSON.stringify([
    filter.amenities ?? null,
    filter.priceRanges ?? null,
    filter.minRating ?? null,
    filter.minCashback ?? null,
    filter.openNow ?? null,
    localityId || null,
  ]);

/**
 * The payload.
 *
 * `slug` travels as typed - **never re-derived from the name**. On creation a
 * blank slug is the server's cue to derive one; on edition the field already
 * holds the current address, so a rename leaves it untouched and the indexed
 * `/explorar/pet-friendly` keeps resolving. The core enforces the same rule
 * (`BackofficeDiscoveryTrailService.update` only moves the address when a
 * *different*, non-blank slug arrives), and this side has to agree: a form that
 * helpfully rewrote the slug while the name was typed would defeat it from
 * outside.
 *
 * Blank text fields go out as `null` rather than `''`, so the column stays null
 * and the site's "fall back to the trail's own tagline" keeps working.
 */
export const toTrailRequest = (form: TrailFormState): TrailRequestDto => ({
  name: form.name.trim(),
  filter: buildTrailFilter(form),
  slug: form.slug.trim() || null,
  tagline: form.tagline.trim() || null,
  icon: form.icon.trim() || null,
  localityId: form.localityId || null,
  startsAt: fromLocalInput(form.startsAt),
  endsAt: fromLocalInput(form.endsAt),
  sortOrder: form.sortOrder,
  active: form.active,
  seoTitle: form.seoTitle.trim() || null,
  seoDescription: form.seoDescription.trim() || null,
  seoKeywords: form.seoKeywords.length > 0 ? form.seoKeywords : null,
});

/**
 * What the curator must fix before the request is worth sending.
 *
 * Only the checks whose answer the screen already knows. Everything the server
 * owns - the amenity catalog, the slug already in use - is left to the server
 * and translated by `TRAIL_ERROR_MESSAGES`: duplicating those here would be a
 * second rule to keep in sync, and the first drift would refuse something the
 * core accepts.
 */
export const validateTrail = (form: TrailFormState): string | null => {
  if (!form.name.trim()) return 'Informe o nome da trilha.';
  if (form.name.trim().length > 80) return 'O nome deve ter no máximo 80 caracteres.';
  if (form.slug.trim().length > 80) return 'O endereço deve ter no máximo 80 caracteres.';
  if (form.tagline.trim().length > 160) return 'A chamada deve ter no máximo 160 caracteres.';
  if (form.seoTitle.trim().length > 160) return 'O título de SEO deve ter no máximo 160 caracteres.';
  if (form.seoDescription.trim().length > 320) {
    return 'A descrição de SEO deve ter no máximo 320 caracteres.';
  }

  if (isFilterEmpty(buildTrailFilter(form))) {
    return 'Escolha ao menos um critério de filtro. Sem nenhum, a trilha seria a vitrine inteira com outro nome.';
  }

  const rating = toNumberOrNull(form.minRating);
  if (form.minRating.trim() && rating === null) return 'A nota mínima precisa ser um número.';
  if (rating !== null && (rating < 0 || rating > 5)) return 'A nota mínima precisa estar entre 0 e 5.';

  const cashback = toNumberOrNull(form.minCashback);
  if (form.minCashback.trim() && cashback === null) return 'O cashback mínimo precisa ser um número.';
  if (cashback !== null && (cashback < 0 || cashback > 100)) {
    return 'O cashback mínimo precisa estar entre 0% e 100%.';
  }

  // A janela invertida é uma trilha que fica "Ativa" na tela e nunca aparece no
  // site. O core recusa com TRAIL_INVALID_WINDOW; avisar aqui poupa a ida e
  // volta e, principalmente, o texto já digitado.
  const startsAt = fromLocalInput(form.startsAt);
  const endsAt = fromLocalInput(form.endsAt);
  if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
    return 'O fim da vigência deve ser depois do início.';
  }

  return null;
};

/**
 * Why a trail is or is not on the site right now.
 *
 * `visible` and `active` are different fields and the screen has to show both:
 * an active trail whose window closed yesterday reads `active: true,
 * visible: false`, and a badge saying only "Ativa" would send the curator
 * looking for the defect on the site. When they disagree, the window is the
 * only possible reason - that is exactly how the core computes `visible` - so
 * the reason can be stated instead of hinted at.
 */
export const describeTrailVisibility = (
  trail: Pick<BackofficeTrailDto, 'active' | 'visible' | 'startsAt' | 'endsAt'>,
  now: Date = new Date()
): { label: string; tone: 'success' | 'warning' | 'secondary'; reason: string | null } => {
  if (!trail.active) {
    return { label: 'Desativada', tone: 'secondary', reason: 'Desativada - não aparece no /explorar.' };
  }
  if (trail.visible) {
    return { label: 'No ar', tone: 'success', reason: null };
  }
  const startsAt = trail.startsAt ? new Date(trail.startsAt) : null;
  if (startsAt && startsAt.getTime() > now.getTime()) {
    return {
      label: 'Agendada',
      tone: 'warning',
      reason: 'Ativa, mas a vigência ainda não começou - só aparece na data de início.',
    };
  }
  const endsAt = trail.endsAt ? new Date(trail.endsAt) : null;
  if (endsAt && endsAt.getTime() <= now.getTime()) {
    return {
      label: 'Vigência encerrada',
      tone: 'warning',
      reason: 'Ativa, mas a vigência terminou - saiu do /explorar na data de fim.',
    };
  }
  // Guarda para o desencontro que não deveria existir: `visible` é do servidor
  // e a janela é o único motivo possível. Se as duas coisas discordarem, dizer
  // "invisível" é mais honesto do que escolher um motivo que não confere.
  return { label: 'Fora do ar', tone: 'warning', reason: 'Ativa, mas o servidor não a considera vigente agora.' };
};

/**
 * What the count means for publication.
 *
 * The floor is [TRAIL_MIN_MERCHANTS] and it does **not** come in the response:
 * the preview answers `{count}` and nothing else, and the server does not
 * refuse to save below it - the count grows on its own as stores join, and
 * refusing would stop curation from preparing the ground. So the warning is the
 * screen's job, and without it someone creates a trail that never appears.
 */
export const describeTrailAudience = (
  count: number
): { message: string; publishable: boolean } => {
  if (count >= TRAIL_MIN_MERCHANTS) {
    return {
      message: `${count} ${count === 1 ? 'loja' : 'lojas'} nesta trilha - ela será publicada e indexada.`,
      publishable: true,
    };
  }
  return {
    message:
      count === 0
        ? `Nenhuma loja atende a este filtro. Abaixo de ${TRAIL_MIN_MERCHANTS} lojas a trilha não é publicada nem indexada: dá para salvar, mas ela não vai aparecer no /explorar.`
        : `Só ${count} loja atende a este filtro. Abaixo de ${TRAIL_MIN_MERCHANTS} lojas a trilha não é publicada nem indexada: dá para salvar, mas ela não vai aparecer no /explorar.`,
    publishable: false,
  };
};

/**
 * The address the server would derive from a name.
 *
 * A transcription of `Slugs.of` in the core (NFD, accents dropped, lowercase,
 * anything else collapsed into single hyphens, trimmed, cut at 80 on a word
 * boundary, `trilha` when nothing usable is left). It exists **only to show the
 * curator what will happen** when the slug field is left blank - it is never
 * written into the field, because that is precisely how a rename would start
 * moving an indexed URL.
 */
export const previewSlug = (name: string): string => {
  const normalized = name
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!normalized) return 'trilha';
  if (normalized.length <= 80) return normalized;
  const window = normalized.slice(0, 81);
  const lastSeparator = window.lastIndexOf('-');
  const cut = lastSeparator > 0 ? window.slice(0, lastSeparator) : normalized.slice(0, 80);
  return cut.replace(/^-+|-+$/g, '');
};
